/**
 * Opening a direct connection to another session, in both directions.
 *
 * The server introduces two sessions and passes a few hundred bytes between them; from
 * there they talk to each other and the server is not involved. What crosses this file is
 * that introduction: an offer, an answer, and a stream of candidate routes.
 *
 * ## Why a connection can simply fail
 *
 * Two browsers behind different routers each know only a private address that means
 * nothing on the other side. A STUN server tells each what its public address looks like,
 * which is enough for most home networks. It is not enough when both ends are behind a
 * router that assigns a different public port per destination — a symmetric NAT — because
 * then the address one end learns is not the address the other end would have to send to.
 * Getting through that needs a relay, which is a server that carries all the traffic, and
 * running one is a different undertaking from running a signalling endpoint.
 *
 * So failing is a supported outcome here, not a bug, and [`NotReachableError`] exists to
 * say so in a way the interface can explain.
 *
 * ## Both directions at once
 *
 * Either session may start a share, and both may start one at the same moment. Rather than
 * arbitrating that, each attempt carries its own random name and gets its own peer
 * connection, so two simultaneous shares between the same pair are simply two connections.
 * They both converge on the same answer, because what to copy is decided by timestamps
 * rather than by who asked first.
 */

import { logger } from '../log.js';
import {
  describeCandidateError,
  describeIceServers,
  describeReflexive,
  describeRoute,
  reachabilityNote,
  selectedRoute,
  summariseCandidates,
} from './iceDiagnostics.js';
import type { Signalling, IceConfig } from './signalling.js';

const log = logger('share');

/**
 * How long to spend trying to reach a peer before giving up.
 *
 * Candidate gathering against a STUN server is a second or two on a good day. Fifteen is
 * generous enough not to fail a slow-but-working connection, and short enough that a
 * hopeless one does not leave somebody watching a spinner.
 */
const CONNECT_TIMEOUT_MS = 15_000;

/** The channel a share talks over. */
const SHARE_CHANNEL = 'models';

/**
 * The channel that only asks whether a connection is possible.
 *
 * Named rather than inferred, because the answering side has to tell the two apart before
 * it has been told anything: a share sets up a library and a conversation, and a probe
 * wants neither. The label is the first thing it knows about a channel.
 */
export const PROBE_CHANNEL = 'probe';

/** Raised when no route between the two browsers could be found. */
export class NotReachableError extends Error {
  constructor(readonly peer: string) {
    super('No direct connection to that session could be opened.');
    this.name = 'NotReachableError';
  }
}

interface Attempt {
  readonly connection: RTCPeerConnection;
  /** Candidates that arrived before there was a description to attach them to. */
  readonly early: RTCIceCandidateInit[];
  /**
   * The kinds of route found for our own end, in the order they were found.
   *
   * Kept only so that a failure can say what was tried. Without it, giving up reports
   * nothing but the fact of giving up, which tells nobody whether STUN was the problem.
   */
  readonly gathered: (string | null)[];
}

export class PeerLinks {
  private ice: IceConfig = { urls: [] };

  /** Attempts this session started, by peer and attempt name. */
  private readonly outgoing = new Map<string, Attempt>();
  /** Attempts the other session started. */
  private readonly incoming = new Map<string, Attempt>();

  /** Which peer connection a channel belongs to, so both can be closed together. */
  private readonly connections = new WeakMap<RTCDataChannel, RTCPeerConnection>();

  constructor(
    private readonly signalling: Signalling,
    /** Called with a channel opened by the *other* session, which we answer. */
    private readonly onIncomingChannel: (peer: string, channel: RTCDataChannel) => void,
  ) {}

  setIceConfig(ice: IceConfig): void {
    this.ice = ice;

    // An empty list is silently survivable and quietly crippling: connections are still
    // attempted, they still work between two browsers on one network, and they fail
    // everywhere else for a reason nothing else would report.
    if (ice.urls.length === 0) {
      log.warn(
        'The server offered no STUN server, so a connection can only be opened to a ' +
          'browser on this same network.',
      );
      return;
    }

    log.info(`Routes will be looked for using ${describeIceServers(ice)}.`);
  }

  /** Drop every connection, as signing out or losing the signalling socket should. */
  closeAll(): void {
    for (const attempt of [...this.outgoing.values(), ...this.incoming.values()]) {
      attempt.connection.close();
    }
    this.outgoing.clear();
    this.incoming.clear();
  }

  /**
   * Open a channel to another session.
   *
   * Rejects with [`NotReachableError`] if no route can be found, which on a home network
   * usually means the two ends are not on the same one.
   */
  async open(peer: string, label: string = SHARE_CHANNEL): Promise<RTCDataChannel> {
    const attempt = randomName();
    const key = `${peer}:${attempt}`;

    const connection = new RTCPeerConnection(this.configuration());
    const held: Attempt = { connection, early: [], gathered: [] };
    this.outgoing.set(key, held);

    // Ordered and reliable, which are the defaults and are both required: the file is
    // reassembled from chunks in the order they were sent.
    const channel = connection.createDataChannel(label, { ordered: true });
    this.connections.set(channel, connection);

    this.abandonWhenLost(connection, () => {
      this.outgoing.delete(key);
    });
    this.forwardCandidates(held, peer, attempt);
    this.report(held, peer);

    try {
      const offer = await connection.createOffer();
      await connection.setLocalDescription(offer);
      this.signalling.send(peer, {
        kind: 'offer',
        attempt,
        sdp: connection.localDescription?.sdp ?? offer.sdp,
      });

      await untilOpen(connection, channel, peer);
      return channel;
    } catch (error) {
      // The one failure a user actually sees, so it is worth saying what was tried before
      // giving up rather than only that we did.
      if (error instanceof NotReachableError) {
        log.warn(
          `Gave up reaching ${peer}. Routes found here: ${summariseCandidates(held.gathered)}.`,
        );
      }

      connection.close();
      this.outgoing.delete(key);
      throw error;
    }
  }

  /**
   * Find out whether a session can be reached at all, without sharing anything with it.
   *
   * Exactly the negotiation [`open`] makes, on a differently labelled channel, hung up the
   * moment it succeeds. Nothing cheaper would answer the question: the only proof that two
   * browsers can reach each other is two browsers reaching each other, and the failure this
   * is looking for — a NAT that no amount of STUN gets through — happens at the very last
   * step, after everything up to and including the candidate exchange has gone perfectly.
   *
   * Resolves if a route exists and rejects with [`NotReachableError`] if none does, which
   * is the same pair of outcomes a share reports and is deliberately the same code path.
   */
  async probe(peer: string): Promise<void> {
    const channel = await this.open(peer, PROBE_CHANNEL);
    this.release(channel);
  }

  /**
   * Let go of a link once its conversation is over.
   *
   * Closing the channel alone leaves the peer connection and its ICE machinery alive, and
   * a session that shares repeatedly would accumulate one of those per share.
   */
  release(channel: RTCDataChannel): void {
    channel.close();
    this.connections.get(channel)?.close();
  }

  /** Handle something the server passed on from another session. */
  handleSignal(from: string, payload: unknown): void {
    const message = readSignal(payload);
    if (!message) return;

    void this.dispatch(from, message).catch((error: unknown) => {
      log.warn('A sharing connection could not be negotiated.', error);
    });
  }

  private async dispatch(from: string, message: SignalMessage): Promise<void> {
    const key = `${from}:${message.attempt}`;

    switch (message.kind) {
      case 'offer':
        await this.answer(from, message.attempt, message.sdp);
        return;

      case 'answer': {
        const attempt = this.outgoing.get(key);
        if (!attempt) return;
        await attempt.connection.setRemoteDescription({
          type: 'answer',
          sdp: message.sdp,
        });
        await drainEarly(attempt);
        return;
      }

      case 'candidate': {
        const attempt = this.outgoing.get(key) ?? this.incoming.get(key);
        if (!attempt) return;
        // A candidate can arrive before the description it belongs to. Adding it then is
        // an error, so it waits.
        if (attempt.connection.remoteDescription === null) {
          attempt.early.push(message.candidate);
          return;
        }
        await attempt.connection.addIceCandidate(message.candidate);
        return;
      }
    }
  }

  private async answer(from: string, attempt: string, sdp: string): Promise<void> {
    const key = `${from}:${attempt}`;
    if (this.incoming.has(key)) return;

    const connection = new RTCPeerConnection(this.configuration());
    const held: Attempt = { connection, early: [], gathered: [] };
    this.incoming.set(key, held);

    connection.addEventListener('datachannel', (event) => {
      this.connections.set(event.channel, connection);
      this.onIncomingChannel(from, event.channel);
    });

    this.abandonWhenLost(connection, () => {
      this.incoming.delete(key);
    });

    this.forwardCandidates(held, from, attempt);
    this.report(held, from);

    await connection.setRemoteDescription({ type: 'offer', sdp });
    await drainEarly(held);

    const answer = await connection.createAnswer();
    await connection.setLocalDescription(answer);

    this.signalling.send(from, {
      kind: 'answer',
      attempt,
      sdp: connection.localDescription?.sdp ?? answer.sdp,
    });
  }

  /**
   * Close a connection that has failed, rather than merely forgetting about it.
   *
   * Forgetting is not enough, and this was measured rather than reasoned about: when a peer
   * vanishes mid-transfer, dropping our reference leaves the data channel open as far as
   * everything above is concerned, so the conversation using it is never told and waits for
   * a message that cannot arrive — holding whatever it had received so far, which for a
   * large model is a great deal of memory. Closing the connection closes the channel, which
   * is what the conversation is listening for.
   *
   * Only on `failed`, not on `disconnected`: the latter is often a passing blip that ICE
   * recovers from on its own, and tearing down a connection that was about to come back
   * would turn a hiccup into a failed share.
   */
  private abandonWhenLost(connection: RTCPeerConnection, forget: () => void): void {
    connection.addEventListener('connectionstatechange', () => {
      const state = connection.connectionState;
      if (state !== 'failed' && state !== 'closed') return;

      forget();
      connection.close();
    });
  }

  private forwardCandidates(held: Attempt, peer: string, attempt: string): void {
    held.connection.addEventListener('icecandidate', (event) => {
      // A null candidate means gathering has finished, which needs no message.
      if (!event.candidate) return;

      held.gathered.push(event.candidate.type);

      // The line somebody diagnosing a failed share is looking for: proof that the STUN
      // server replied, and what it said this browser looks like from outside.
      if (event.candidate.type === 'srflx') {
        log.info(describeReflexive(event.candidate));
      }

      this.signalling.send(peer, {
        kind: 'candidate',
        attempt,
        candidate: event.candidate.toJSON(),
      });
    });
  }

  /**
   * Say what happened to an attempt, without changing what happens.
   *
   * Separate from [`forwardCandidates`] because the two answer to different things: that
   * one is the protocol, and losing a line of it breaks a share. Nothing here has any
   * effect beyond the console, and all of it is safe to remove.
   */
  private report(held: Attempt, peer: string): void {
    const connection = held.connection;

    connection.addEventListener('icecandidateerror', (event) => {
      log.warn(describeCandidateError(event));
    });

    connection.addEventListener('icegatheringstatechange', () => {
      if (connection.iceGatheringState !== 'complete') return;

      log.info(
        `Finished looking for routes to ${peer}: ${summariseCandidates(held.gathered)}.`,
      );

      const note = reachabilityNote(held.gathered);
      if (note !== undefined) log.warn(note);
    });

    connection.addEventListener('connectionstatechange', () => {
      if (connection.connectionState !== 'connected') return;
      void this.describeConnection(connection, peer);
    });
  }

  /**
   * How a connection that worked was actually made.
   *
   * The most useful line on a *successful* share, because it distinguishes "STUN got us
   * through a NAT" from "these two happened to be on the same network anyway" — and the
   * second of those looks like a working feature right up until somebody tries it from
   * elsewhere.
   */
  private async describeConnection(
    connection: RTCPeerConnection,
    peer: string,
  ): Promise<void> {
    try {
      const entries: unknown[] = [];
      const stats = await connection.getStats();
      stats.forEach((value: unknown) => {
        entries.push(value);
      });

      const route = selectedRoute(entries);
      if (route === undefined) {
        log.debug(`Connected to ${peer}, but the chosen route could not be read.`);
        return;
      }

      log.info(`Connected to ${peer} using ${describeRoute(route)}.`);
    } catch (error) {
      // Reporting must not be able to break a connection that has just succeeded, and a
      // connection closed between the event and the query would do exactly that.
      log.debug('The route to a peer could not be reported.', error);
    }
  }

  private configuration(): RTCConfiguration {
    if (this.ice.urls.length === 0) return { iceServers: [] };

    const server: RTCIceServer = {
      urls: [...this.ice.urls],
      ...(this.ice.username === undefined ? {} : { username: this.ice.username }),
      ...(this.ice.credential === undefined ? {} : { credential: this.ice.credential }),
    };

    return { iceServers: [server] };
  }
}

/** Wait for the channel to open, or for the attempt to be hopeless. */
async function untilOpen(
  connection: RTCPeerConnection,
  channel: RTCDataChannel,
  peer: string,
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const timer = setTimeout(() => {
      settle(() => {
        reject(new NotReachableError(peer));
      });
    }, CONNECT_TIMEOUT_MS);

    const settle = (finish: () => void): void => {
      clearTimeout(timer);
      channel.removeEventListener('open', onOpen);
      connection.removeEventListener('connectionstatechange', onState);
      finish();
    };

    const onOpen = (): void => {
      settle(resolve);
    };
    const onState = (): void => {
      if (connection.connectionState === 'failed') {
        settle(() => {
          reject(new NotReachableError(peer));
        });
      }
    };

    if (channel.readyState === 'open') {
      settle(resolve);
      return;
    }

    channel.addEventListener('open', onOpen);
    connection.addEventListener('connectionstatechange', onState);
  });
}

async function drainEarly(attempt: Attempt): Promise<void> {
  const waiting = attempt.early.splice(0, attempt.early.length);
  for (const candidate of waiting) {
    try {
      await attempt.connection.addIceCandidate(candidate);
    } catch (error) {
      // One unusable candidate does not doom the connection; the others may still work.
      log.debug('Discarded an unusable connection candidate.', error);
    }
  }
}

type SignalMessage =
  | { readonly kind: 'offer'; readonly attempt: string; readonly sdp: string }
  | { readonly kind: 'answer'; readonly attempt: string; readonly sdp: string }
  | {
      readonly kind: 'candidate';
      readonly attempt: string;
      readonly candidate: RTCIceCandidateInit;
    };

function readSignal(payload: unknown): SignalMessage | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined;
  const body = payload as Record<string, unknown>;

  const attempt = typeof body.attempt === 'string' ? body.attempt : undefined;
  if (attempt === undefined) return undefined;

  if (body.kind === 'offer' || body.kind === 'answer') {
    return typeof body.sdp === 'string'
      ? { kind: body.kind, attempt, sdp: body.sdp }
      : undefined;
  }

  if (body.kind === 'candidate') {
    const candidate = body.candidate;
    if (typeof candidate !== 'object' || candidate === null) return undefined;
    return { kind: 'candidate', attempt, candidate };
  }

  return undefined;
}

function randomName(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  return Math.random().toString(36).slice(2);
}
